import { useMemo } from 'react';
import { Clock, User, Calendar } from 'lucide-react';

/**
 * Riepilogo ore agenti calcolato client-side dai dati già caricati in CaseDetail.
 * - Mostra TUTTI gli agenti assegnati al caso (anche quelli senza ore ancora registrate)
 * - Si aggiorna automaticamente quando caseData o activityDays cambiano
 * - Conta solo i giorni effettivamente schedulati per ciascun agente
 */
export default function AgentHoursSummary({ caseData, activityDays = [] }) {
    const summary = useMemo(() => {
        if (!caseData) return [];

        const assignedIds = caseData.assigned_agents || [];
        const agentsInfo = caseData.assigned_agents_info || {};
        const schedules = caseData.agent_schedules || {};

        // Aggrega ore da tutte le giornate
        const hoursByAgent = {};
        for (const day of activityDays) {
            for (const ah of (day.agent_hours || [])) {
                const aid = ah.agent_id;
                if (!aid) continue;
                if (!hoursByAgent[aid]) hoursByAgent[aid] = { total: 0, days: 0, name: ah.agent_name || '' };
                hoursByAgent[aid].total += (ah.hours || 0);
                if ((ah.hours || 0) > 0) hoursByAgent[aid].days += 1;
                if (!hoursByAgent[aid].name && ah.agent_name) hoursByAgent[aid].name = ah.agent_name;
            }
        }

        // Costruisci la lista: tutti gli agenti assegnati + eventuali agenti ex-assegnati
        // che hanno ancora ore storiche salvate.
        const uniqueIds = new Set([...assignedIds, ...Object.keys(hoursByAgent)]);

        const result = [];
        for (const aid of uniqueIds) {
            const info = agentsInfo[aid] || {};
            const rec = hoursByAgent[aid] || { total: 0, days: 0, name: '' };
            const name = rec.name || info.full_name || info.email || 'Agente';
            // Giorni schedulati per questo agente su questo caso
            const scheduledDays = (schedules[aid]?.dates || []).length;
            const isAssigned = assignedIds.includes(aid);
            result.push({
                agent_id: aid,
                agent_name: name,
                total_hours: rec.total,
                days_worked: rec.days,
                scheduled_days: scheduledDays,
                is_assigned: isAssigned,
            });
        }

        // Ordina: prima gli assegnati, poi per nome
        result.sort((a, b) => {
            if (a.is_assigned !== b.is_assigned) return a.is_assigned ? -1 : 1;
            return (a.agent_name || '').localeCompare(b.agent_name || '');
        });

        return result;
    }, [caseData, activityDays]);

    if (!caseData || summary.length === 0) {
        return null;
    }

    const totalHours = summary.reduce((sum, agent) => sum + agent.total_hours, 0);

    return (
        <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="agent-hours-summary">
            <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#00FF41]" />
                Riepilogo Ore Agenti
            </h3>

            <div className="space-y-3">
                {summary.map(agent => (
                    <div
                        key={agent.agent_id}
                        data-testid={`agent-hours-summary-row-${agent.agent_id}`}
                        className={`flex items-center justify-between p-3 bg-black/30 border ${
                            agent.is_assigned ? 'border-white/5' : 'border-yellow-500/30'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#00FF41]/20 border border-[#00FF41]/50 flex items-center justify-center">
                                <User className="w-4 h-4 text-[#00FF41]" />
                            </div>
                            <div>
                                <p className="text-white font-medium flex items-center gap-2">
                                    {agent.agent_name}
                                    {!agent.is_assigned && (
                                        <span
                                            className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"
                                            title="Agente non più assegnato, ma con ore storiche"
                                        >
                                            ex
                                        </span>
                                    )}
                                </p>
                                <p className="text-white/40 text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {agent.days_worked} giorn{agent.days_worked === 1 ? 'o' : 'i'} con ore
                                    {agent.scheduled_days > 0 && (
                                        <span className="ml-1 text-white/30">
                                            / {agent.scheduled_days} schedulat{agent.scheduled_days === 1 ? 'o' : 'i'}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-heading font-bold text-[#00FF41]">
                                {agent.total_hours.toFixed(1)}
                            </p>
                            <p className="text-white/40 text-xs uppercase">ore totali</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Totale complessivo */}
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-white/60 uppercase text-sm">Totale Ore Caso</span>
                <span className="text-3xl font-heading font-bold text-white">
                    {totalHours.toFixed(1)} <span className="text-sm text-white/40">ore</span>
                </span>
            </div>
        </div>
    );
}
